import { forwardRef } from 'react';

import type {
  WithChildren,
  MaskingProps,
  AriaLabelingProps,
  DataTestId,
} from '@dynatrace/strato-components/core';

import * as styles from './SuggestionsOverlay.sty.js';
import { SuggestionTips } from './SuggestionTips.js';
import { OverlayContainer } from '../../../core/components/OverlayContainer.js';
import { useOverlayStacking } from '../../../core/hooks/useOverlayStacking.js';
import { OverlayStackingProvider } from '../../../core/providers/OverlayStackingProvider.js';
import type { ModalAriaProps } from '../../../overlays/hooks/useOverlayWithTrigger.js';

/**
 * Accepted properties for FilterField suggestion overlay.
 * @public
 */
export interface SuggestionsOverlayProps
  extends AriaLabelingProps,
    WithChildren,
    DataTestId,
    MaskingProps {
  /** Whether the overlay is open. */
  isOpen: boolean;
  /**
   * Properties for connecting the overlay to the overlay trigger.
   * Includes styles and accessibility attributes.
   */
  overlayProps?: ModalAriaProps;
}

/**
 * @public
 */
export const SuggestionsOverlay = forwardRef<
  HTMLDivElement,
  SuggestionsOverlayProps
>((props, forwardedRef) => {
  const {
    isOpen,
    children,
    overlayProps,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props;

  const overlayContainerProps = useOverlayStacking(undefined, 'dropdown');

  if (!isOpen) {
    return null;
  }

  return (
    <OverlayContainer
      className="suggestions-overlay"
      {...overlayContainerProps}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
    >
      <OverlayStackingProvider>
        <div
          ref={forwardedRef}
          data-testid={dataTestId}
          className={styles.suggestionsOverlayWrapper}
          onPointerDown={(event) => {
            // Prevent default on the suggestions overlay to avoid filter field
            // losing focus when clicking on an entry in the overlay.
            event.preventDefault();
          }}
          {...overlayProps}
        >
          {children}
          <SuggestionTips />
        </div>
      </OverlayStackingProvider>
    </OverlayContainer>
  );
});
